// Cliente acompanhando o guincho (pedido 2026-10-16) — link enviado pela
// central, SEM login (token na URL; bypass em __root.tsx). Mostra onde o
// guincho está, a etapa e a previsão de chegada (rota com trânsito, mesmo
// provedor do /ao-vivo). Atualiza sozinho a cada poucos segundos.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { CheckCircle2, Clock, Loader2, MapPin, Phone, TriangleAlert, Truck } from "lucide-react";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { gpsDriveAssistancePublicService as api, type PublicTrackView } from "@/services/gpsdrive-assistance-center.service";
import { AssistanceMap, fmtDistance, fmtDuration } from "@/components/assistance-public-map";

export const Route = createFileRoute("/acompanhar/$token")({
  component: TrackPage,
  head: () => ({ meta: [{ title: "Acompanhe seu guincho" }, { name: "robots", content: "noindex" }] }),
});

const POLL_MS = 5_000;
const STEPS: { key: PublicTrackView["phase"]; label: string }[] = [
  { key: "aguardando", label: "Solicitado" },
  { key: "a_caminho", label: "A caminho" },
  { key: "no_local", label: "No local" },
  { key: "levando", label: "Levando ao destino" },
  { key: "concluido", label: "Concluído" },
];
const TRUCK_TYPE: Record<string, string> = { plataforma: "Plataforma", asa_delta: "Asa delta", reboque: "Reboque", pesado: "Pesado", moto: "Moto", outro: "Guincho" };

function TrackPage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<PublicTrackView | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [netError, setNetError] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      setData(await api.track(token));
      setNetError(false);
    } catch (e) {
      if (e instanceof GpsDriveApiError && e.status === 404) setNotFound(true);
      else setNetError(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
    const t = setInterval(() => !document.hidden && void load(), POLL_MS);
    const c = setInterval(() => setNow(Date.now()), 15_000);
    return () => {
      clearInterval(t);
      clearInterval(c);
    };
  }, [load]);

  const path = useMemo(() => data?.eta?.geometry.map(([lat, lng]) => ({ lat, lng })) ?? null, [data?.eta]);

  if (notFound) return <Full icon={<TriangleAlert className="h-10 w-10 text-amber-500" />} title="Link inválido" text="Este link de acompanhamento não existe." />;
  if (!data)
    return (
      <Full
        icon={netError ? <TriangleAlert className="h-10 w-10 text-amber-500" /> : <Loader2 className="h-10 w-10 animate-spin text-blue-600" />}
        title={netError ? "Sem conexão" : "Carregando…"}
        text={netError ? "Verifique a internet. Tentando de novo…" : ""}
      />
    );

  const a = data.vehiclePosition ? { lat: data.vehiclePosition.lat, lng: data.vehiclePosition.lng } : null;
  const b = data.destination ? { lat: data.destination.lat, lng: data.destination.lng } : null;
  const tow = data.towPosition ? { lat: data.towPosition.lat, lng: data.towPosition.lng } : null;
  const arrival = data.eta ? new Date(now + data.eta.durationS * 1000) : null;
  const stepIndex = Math.max(0, STEPS.findIndex((s) => s.key === data.phase));
  const stale = data.towPosition?.at ? now - new Date(data.towPosition.at).getTime() > 90_000 : false;

  const headline =
    data.phase === "aguardando"
      ? "Estamos acionando o guincho"
      : data.phase === "a_caminho"
        ? "O guincho está a caminho"
        : data.phase === "no_local"
          ? "O guincho chegou ao seu veículo"
          : data.phase === "levando"
            ? "Seu veículo está sendo levado ao destino"
            : data.phase === "concluido"
              ? "Atendimento concluído"
              : "Atendimento cancelado";

  return (
    <div className="flex h-[100dvh] flex-col bg-slate-100 text-slate-900">
      <div className="relative min-h-0 flex-1">
        <AssistanceMap a={a} b={b} tow={tow} towHeading={data.towPosition?.heading} route={path} className="h-full w-full" />
        <div className="absolute inset-x-3 rounded-2xl bg-white/95 px-4 py-3 shadow-lg backdrop-blur" style={{ top: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
          <div className="text-xs font-medium text-slate-500">Olá, {data.clientFirstName}</div>
          <div className="text-base font-bold leading-tight">{headline}</div>
        </div>
      </div>

      <div className="space-y-4 rounded-t-3xl bg-white px-5 pt-5 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}>
        {data.eta && (data.phase === "a_caminho" || data.phase === "levando") ? (
          <div className="flex items-end justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{data.eta.target === "veiculo" ? "Chega até você em" : "Chega ao destino em"}</div>
              <div className="text-4xl font-black tracking-tight text-blue-600">{fmtDuration(data.eta.durationS)}</div>
              <div className="text-sm text-slate-500">
                {fmtDistance(data.eta.distanceM)}
                {arrival && ` · previsão ${arrival.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`}
              </div>
            </div>
            <Clock className="mb-2 h-10 w-10 text-blue-100" />
          </div>
        ) : data.phase === "concluido" ? (
          <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 font-semibold text-emerald-700">
            <CheckCircle2 className="h-5 w-5" /> Seu veículo foi entregue. Obrigado!
          </div>
        ) : data.phase === "a_caminho" && !tow ? (
          <p className="text-sm text-slate-500">Aguardando a localização do guincho…</p>
        ) : null}
        {stale && data.phase === "a_caminho" && <p className="text-xs text-amber-600">A localização do guincho não atualiza há mais de 1 minuto (sinal fraco).</p>}

        {/* Linha do tempo */}
        {data.phase !== "cancelado" && (
          <ol className="flex items-start">
            {STEPS.map((s, i) => (
              <li key={s.key} className="flex flex-1 flex-col items-center text-center">
                <div className="flex w-full items-center">
                  <span className={`h-0.5 flex-1 ${i === 0 ? "opacity-0" : i <= stepIndex ? "bg-blue-600" : "bg-slate-200"}`} />
                  <span className={`grid h-6 w-6 place-items-center rounded-full text-[10px] font-bold ${i <= stepIndex ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {i < stepIndex ? "✓" : i + 1}
                  </span>
                  <span className={`h-0.5 flex-1 ${i === STEPS.length - 1 ? "opacity-0" : i < stepIndex ? "bg-blue-600" : "bg-slate-200"}`} />
                </div>
                <span className={`mt-1 text-[10px] leading-tight ${i === stepIndex ? "font-bold text-slate-900" : "text-slate-500"}`}>{s.label}</span>
              </li>
            ))}
          </ol>
        )}

        {data.towTruck && (
          <Row icon={<Truck className="h-5 w-5 text-blue-600" />}>
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{data.towTruck.name}</div>
              <div className="text-xs text-slate-500">
                {[data.towTruck.type ? TRUCK_TYPE[data.towTruck.type] : null, data.towTruck.plate].filter(Boolean).join(" · ") || "Guincho"}
              </div>
            </div>
            {data.towTruck.phone && (
              <a href={`tel:${data.towTruck.phone.replace(/\D/g, "")}`} className="grid h-10 w-10 place-items-center rounded-full bg-blue-600 text-white" aria-label="Ligar para o guincho">
                <Phone className="h-4 w-4" />
              </a>
            )}
          </Row>
        )}
        <Row icon={<span className="grid h-5 w-5 place-items-center rounded-full bg-[#e0731f] text-[10px] font-black text-white">A</span>}>
          <div className="min-w-0">
            <div className="text-xs text-slate-500">Seu veículo</div>
            <div className="truncate text-sm font-semibold">{[data.vehicle.model ?? data.vehicle.label, data.vehicle.color, data.vehicle.plate].filter(Boolean).join(" · ")}</div>
          </div>
        </Row>
        {data.destination && (
          <Row icon={<MapPin className="h-5 w-5 text-[#7c5cd6]" />}>
            <div className="min-w-0">
              <div className="text-xs text-slate-500">Destino</div>
              <div className="text-sm font-semibold">{data.destination.address ?? "Ponto B"}</div>
            </div>
          </Row>
        )}
      </div>
    </div>
  );
}

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 border-t pt-3">
      <span className="shrink-0">{icon}</span>
      {children}
    </div>
  );
}

function Full({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 bg-slate-100 p-8 text-center text-slate-900">
      {icon}
      <h1 className="text-xl font-bold">{title}</h1>
      {text && <p className="max-w-xs text-sm text-slate-500">{text}</p>}
    </div>
  );
}
